import React, { useState } from 'react';
import getValue from 'lodash.get';
import Typography from '@mui/material/Typography';
import idScan from './assets/id-scan.json';
import ScanningPage from './components/ScanningPage.jsx';
import SectionContainer from '../layout/SectionContainer.jsx';
import SectionNav from '../layout/SectionNav.jsx';
import WelcomeScreen from '../layout/WelcomeScreen.jsx';
import { getTextDefaults } from './translations/DocumentSectionTranslation.js';

const WelcomePage = ({ text }) => {
	return (
		<WelcomeScreen animation={idScan}>
			<Typography variant="h6">{text.welcomeTitle}</Typography>
			<Typography>{text.welcomeReason}</Typography>
		</WelcomeScreen>
	);
};

const DocumentSection = ({
	possibleDocuments = [],
	requiredDocuments = [],
	documentTypeForMultipleTypes,
	minValidityPeriod = false,
	onChange = () => {},
	values = {},
	labels = [],
	textOverride = {},
	uploadUrl = '/api/files',
	background = 'light',
	name = 'userDocuments',
	selectedLanguage = 'EN',
	noWelcome = false,
	addPendingAction = () => {},
	removePendingAction = () => {},
	useCamera = true,
	cameraIncludes = '',
	cameraRotationAngle = 0,
	disabled = false,
	navigationProps,
	style = {},
}) => {
	const text = Object.assign({}, getTextDefaults(selectedLanguage), textOverride);
	const value = getValue(values, name) || [];
	const [currentScreen, setCurrentScreen] = useState(noWelcome ? 'scanning' : 'welcome');

	let uploadContext;
	if (values?.userData?.firstName || values?.userData?.lastName) {
		uploadContext = { userData: { firstName: values.userData.firstName, lastName: values.userData.lastName } };
	}
	if (values?.companyData?.companyName) {
		uploadContext = { ...uploadContext, companyData: { companyName: values.companyData.companyName } };
	}

	const onChangeHandler = (index, processedDoc) => {
		const newValue = [...value];
		newValue[index] = processedDoc;
		return onChange({
			target: {
				name,
				value: newValue,
			},
		});
	};

	const handleNext = (event) => {
		if (currentScreen === 'welcome') {
			setCurrentScreen('scanning');
		}

		if (currentScreen === 'scanning') {
			return navigationProps.next(event);
		}
	};

	const handlePrevious = (event) => {
		if (currentScreen === 'scanning' && !noWelcome) {
			return setCurrentScreen('welcome');
		}

		return navigationProps.previous(event);
	};

	const canContinue =
		currentScreen !== 'scanning' ||
		requiredDocuments.filter((d) => {
			// OR-group (nested array): satisfied when ANY of the grouped types has been uploaded.
			if (Array.isArray(d)) return !value.find((v) => d.includes(v?.type));
			return !value.find((v) => {
				if (v?.type === d) return true;
				if (v?.fileName === d) return true;
				if (d === 'any-id' && ['id', 'passport'].includes(v?.type)) return true;
				return false;
			});
		}).length === 0;

	const hasPrevious = navigationProps.hasPrevious || (currentScreen === 'scanning' && !noWelcome);

	return (
		<SectionContainer
			background={background}
			style={style}
			Navigation={
				<SectionNav
					{...navigationProps}
					hasPrevious={hasPrevious}
					canContinue={canContinue}
					hasNext={currentScreen === 'welcome' || navigationProps.hasNext}
					next={handleNext}
					selectedLanguage={selectedLanguage}
					previous={handlePrevious}
				/>
			}
		>
			{currentScreen === 'welcome' && <WelcomePage text={text} />}
			{currentScreen === 'scanning' && (
				<ScanningPage
					possibleDocuments={possibleDocuments}
					requiredDocuments={requiredDocuments}
					documentTypeForMultipleTypes={documentTypeForMultipleTypes}
					onChange={onChangeHandler}
					minValidityPeriod={minValidityPeriod}
					value={value}
					labels={labels}
					text={text}
					uploadUrl={uploadUrl}
					addPendingAction={addPendingAction}
					removePendingAction={removePendingAction}
					useCamera={useCamera}
					cameraIncludes={cameraIncludes}
					cameraRotationAngle={cameraRotationAngle}
					disabled={disabled}
					context={uploadContext}
				/>
			)}
		</SectionContainer>
	);
};

DocumentSection.type = 'DocumentSection';
DocumentSection.hasNavigation = true;
DocumentSection.spec = [
	{
		name: 'DocumentSection',
		key: 'DocumentSection',
		title: 'Section to upload documents',
		translatable: [],
		allowedFields: [],
		fields: [
			{
				type: 'TextField',
				name: 'title',
				label: 'Section title',
			},
			{
				type: 'AutoCompleteByID',
				name: 'name',
				label: 'Target',
				multiple: false,
				options: [
					{ id: 'userDocuments', label: 'User' },
					{ id: 'groupDocuments', label: 'Group' },
					{ id: 'eventDocuments', label: 'Event' },
				],
				required: true,
			},
			{
				type: 'AsyncAutoComplete',
				name: 'possibleDocuments',
				label: 'Document types that can be uploaded',
				dataSource: '/api/data-sources/document-types/data',
				optionLabel: 'id',
				multiple: true,
				instantSearch: true,
				required: false,
			},
			{
				type: 'AsyncAutoComplete',
				name: 'requiredDocuments',
				label: 'Document types that must be uploaded',
				dataSource: '/api/data-sources/document-types/data',
				optionLabel: 'id',
				multiple: true,
				instantSearch: true,
				required: false,
			},
			{
				type: 'Checkbox',
				label: 'Skip introduction page?',
				name: 'noWelcome',
			},
			{
				type: 'Checkbox',
				name: 'useCamera',
				label: 'Force camera only (kiosk environments only — requires device to be registered)',
			},
		],
		defaultProperties: {
			title: 'Document section',
			noWelcome: false,
			possibleDocuments: ['any-id'],
			refDataMap: {
				'userData.firstName': 'user.firstName',
				'userData.lastName': 'user.lastName',
				'companyData.companyName': 'group.name',
			},
		},
	},
];

export default DocumentSection;
